import { translateText } from './translator';

let isIntlTelInputLoaded = false;

// cache
let cachedCountryCode = null;
let countryCodePromise = null;

async function fetchCountryCode() {
  try {
    let endpoint = '/api/method/zoni_edu.controller.get_country_from_ip';
    if (window.location.host === 'highschool.zoni.edu') {
      endpoint = 'https://www.zoni.edu' + endpoint;
    }
    const resp = await fetch(endpoint, {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await resp.json();
    let countryCode = data.message;
    // lowercase
    countryCode = countryCode.toLowerCase();

    if (countryCode === 'local' || countryCode === 'unknown') {
      countryCode = 'us';
    }
    return countryCode;
  } catch (err) {
    console.error('Error fetching country based on IP:', err);
    return 'us'; // default fallback
  }
}

export async function getCountryCode() {
  if (cachedCountryCode) {
    return cachedCountryCode;
  }

  if (!countryCodePromise) {
    countryCodePromise = (async () => {
      const code = await fetchCountryCode();
      cachedCountryCode = code || 'US';
      return cachedCountryCode;
    })();
  }

  return countryCodePromise;
}

function normalizeIso2(value) {
  return String(value || '').trim().toLowerCase();
}

function isAllowedCountry(countryCode, onlyCountries) {
  if (!Array.isArray(onlyCountries) || onlyCountries.length === 0) {
    return true;
  }
  const allowed = new Set(onlyCountries.map(normalizeIso2).filter(Boolean));
  return allowed.has(normalizeIso2(countryCode));
}

async function waitForIntlTelInput(timeoutMs = 2500) {
  if (window.intlTelInput) {
    return true;
  }

  const startedAt = Date.now();
  return new Promise((resolve) => {
    const timer = window.setInterval(() => {
      if (window.intlTelInput) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 50);
  });
}

function setPhoneInputInvalid(selector, isInvalid) {
  if (!selector) {
    return;
  }
  const input = document.querySelector(selector);
  if (!input) {
    return;
  }
  input.setCustomValidity(isInvalid ? 'invalid' : '');
  input.classList.toggle('is-invalid', Boolean(isInvalid));
}

function selectedCountryIso2(itiInstance) {
  const countryData = itiInstance?.getSelectedCountryData?.();
  return countryData?.iso2 ? String(countryData.iso2).toUpperCase() : null;
}

function resolveInitialCountry(detectedCountry, onlyCountries, initialCountry) {
  const fallback = normalizeIso2(initialCountry) || 'us';
  const detected = normalizeIso2(detectedCountry);
  if (detected && isAllowedCountry(detected, onlyCountries)) {
    return detected;
  }
  if (isAllowedCountry(fallback, onlyCountries)) {
    return fallback;
  }
  if (Array.isArray(onlyCountries) && onlyCountries.length) {
    return normalizeIso2(onlyCountries[0]);
  }
  return fallback;
}

function buildPreferredCountries(preferred, detectedCountry, onlyCountries, initialCountry) {
  const values = [
    ...(Array.isArray(preferred) ? preferred : []),
    detectedCountry,
    initialCountry,
  ].map(normalizeIso2).filter(Boolean);

  return Array.from(new Set(values)).filter((country) => (
    isAllowedCountry(country, onlyCountries)
  ));
}

async function showIntlUnavailable(errorSelector, invalidMessage) {
  const el = errorSelector ? document.querySelector(errorSelector) : null;
  if (!el) {
    return;
  }
  el.textContent = invalidMessage
    || await translateText('Please enter a valid phone number for the selected country.');
  el.style.display = 'block';
}

// Generic validity checker
async function updateValidity(
  itiInstance,
  errorSelector,
  submitBtnSelector = "button[id='zoniModalSubmitButton']",
  selector = null,
  {
    invalidMessage = null,
    onValidityChange = null,
  } = {}
) {
  const isValid = Boolean(itiInstance && itiInstance.isValidNumber());
  const errorEl = errorSelector ? document.querySelector(errorSelector) : null;

  if (isValid) {
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.style.display = 'none';
    }
    if (submitBtnSelector) {
      const button = document.querySelector(submitBtnSelector);
      if (button) {
        button.disabled = false;
      }
    }
    setPhoneInputInvalid(selector, false);
    if (onValidityChange) {
      onValidityChange(true, itiInstance);
    }
    return true;
  }

  const msg = invalidMessage
    || await translateText('Please enter a valid phone number for the selected country.');
  if (errorEl) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }
  if (submitBtnSelector) {
    const button = document.querySelector(submitBtnSelector);
    if (button) {
      button.disabled = true;
    }
  }
  setPhoneInputInvalid(selector, true);
  if (onValidityChange) {
    onValidityChange(false, itiInstance);
  }
  return false;
}

// Initialize an intl-tel-input on a given selector
export async function initIntlPhone({
  selector,
  instanceKey,
  errorSelector = null,
  submitBtnSelector = null,
  preferred = ['us'],
  initialCountry = 'us',
  onlyCountries = null,
  nationalMode = false,
  autoPlaceholder = 'polite',
  formatOnDisplay = true,
  separateDialCode = true,
  invalidMessage = null,
  onValidityChange = null,
  onCountryChange = null
}) {
  const input = document.querySelector(selector);
  if (!input) {
    console.error(`Input element ${selector} not found.`);
    return null;
  }

  const intlReady = await waitForIntlTelInput();
  if (!intlReady) {
    console.warn('intl-tel-input is not available.');
    await showIntlUnavailable(errorSelector, invalidMessage);
    return null;
  }

  const countryCode = await getCountryCode();
  const selectedInitialCountry = resolveInitialCountry(
    countryCode,
    onlyCountries,
    initialCountry
  );
  const preferredCountries = buildPreferredCountries(
    preferred,
    countryCode,
    onlyCountries,
    selectedInitialCountry
  );

  const host = window.location.origin;
  const options = {
    initialCountry: selectedInitialCountry,
    loadUtils: () => import(`${host}/assets/zoni_edu/node_modules/intl-tel-input/build/js/utils.js`),
    autoPlaceholder,
    formatOnDisplay,
    nationalMode,
    separateDialCode,
    preferredCountries
  };
  if (Array.isArray(onlyCountries) && onlyCountries.length) {
    options.onlyCountries = onlyCountries.map(normalizeIso2).filter(Boolean);
  }

  const iti = window.intlTelInput(input, options);

  // Store globally
  window[instanceKey] = iti;

  // Mark loaded
  if (instanceKey === 'iti') {
    isIntlTelInputLoaded = true;
  }

  // Common event bindings
  ['input', 'blur', 'countrychange'].forEach(evt => {
    input.addEventListener(evt, () => {
      if (evt === 'countrychange' && onCountryChange) {
        onCountryChange(selectedCountryIso2(iti), iti);
      }
      if (errorSelector) {
        updateValidity(iti, errorSelector, submitBtnSelector, selector, {
          invalidMessage,
          onValidityChange,
        });
      } else if (onValidityChange) {
        onValidityChange(Boolean(iti.isValidNumber()), iti);
      }
    });
  });

  // Set the detected country only when it is permitted.
  try {
    iti.setCountry(selectedInitialCountry);
  } catch (e) {
    console.warn('Could not set country on intlTelInput:', e);
  }

  return iti;
}

// get the phone number from the intl-tel-input instance
export function getPhoneNumber(instanceKey) {
  const iti = window[instanceKey];
  if (iti && iti.isValidNumber()) {
    return iti.getNumber();
  } else {
    return '';
  }
}

// Check if the phone number from the intl-tel-input instance is valid
export function isPhoneNumberValid(instanceKey) {
  const iti = window[instanceKey];
  if (iti) {
    return iti.isValidNumber();
  } else {
    console.warn(`IntlTelInput instance ${instanceKey} is not initialized.`);
    return false;
  }
}

export function getPhoneCountry(instanceKey) {
  const iti = window[instanceKey];
  if (!iti) {
    return null;
  }
  return selectedCountryIso2(iti);
}

export function getIntlTelInputLoadedStatus() {
  return isIntlTelInputLoaded;
}

export function setIntlTelInputLoadedStatus(status) {
  isIntlTelInputLoaded = status;
}

// Convenience wrappers
export function loadAndInitializeIntlTelInput() {
  return initIntlPhone({
    selector: '#intl_phone_id',
    instanceKey: 'iti',
    errorSelector: '#phone-error',
    submitBtnSelector: "button[id='zoniModalSubmitButton']",
    onValidityChange: (isValid) => {
      document.querySelector('#intl_phone_id')?.dispatchEvent(
        new CustomEvent('zoni:phone-validity-change', {
          detail: { isValid: Boolean(isValid) }
        })
      );
    }
  });
}

export function loadAndInitializeIntlTelApplicationInput(selector = '#student_phone_number') {
  return initIntlPhone({
    selector,
    instanceKey: 'itiApplication',
    errorSelector: '#phone-error-application'
  });
}

export function loadAndInitializeIntlTelGuardianInput(selector = '#guardian_phone_number') {
  return initIntlPhone({
    selector,
    instanceKey: 'itiGuardian',
    errorSelector: '#phone-error-guardian'
  });
}

export function loadAndInitializeIntlTelGuardian2Input(selector = '#guardian2_phone_number') {
  return initIntlPhone({
    selector,
    instanceKey: 'itiGuardian2',
    errorSelector: '#phone-error-guardian2'
  });
}
